import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Generates a stable credential ID from engagement code + user ID
const generateCredentialId = (engagementCode, uid) => {
  let hash = 0;
  const str = `${engagementCode}-${uid}`;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  const num = Math.abs(hash) % 9000 + 1000;
  return `${engagementCode}-${num}`;
};

const formatDate = (ts) => {
  const d = ts ? (ts.toDate ? ts.toDate() : new Date(ts)) : new Date();
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function CertificateGenerator({ user, engagementCode }) {
  const [certData, setCertData] = useState(null);
  const [earned, setEarned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const check = async () => {
      if (!user || !engagementCode) { setLoading(false); return; }
      try {
        const [userSnap, engSnap, wbSnap, assessSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'engagements', engagementCode)),
          getDoc(doc(db, 'workbooks', user.uid)),
          getDoc(doc(db, 'assessments', user.uid)),
        ]);

        const userData = userSnap.exists() ? userSnap.data() : {};
        const engData = engSnap.exists() ? engSnap.data() : {};
        const wbData = wbSnap.exists() ? wbSnap.data() : {};
        const assessData = assessSnap.exists() ? assessSnap.data() : {};

        // Certificate triggers on Final Exam pass.
        // Workbook completion and post-assessment completion are no longer gates.
        // The examPassed flag is set by the scoreExam Cloud Function on the user
        // document when a participant scores 80+ with every section at 15+, or
        // when a TCF admin manually overrides via adminOverrideExam.
        const examPassed = userData.examPassed === true;
        const isEarned = examPassed;

        if (isEarned) {
          // Check if credential ID already exists, if not create and save it
          const certSnap = await getDoc(doc(db, 'certificates', user.uid));
          let credentialId;
          let completedAt;

          if (certSnap.exists()) {
            credentialId = certSnap.data().credentialId;
            completedAt = certSnap.data().completedAt;
          } else {
            credentialId = generateCredentialId(engagementCode, user.uid);
            completedAt = new Date();
            await setDoc(doc(db, 'certificates', user.uid), {
              credentialId,
              completedAt,
              uid: user.uid,
              engagementCode,
              programName: engData.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
              facilitatorName: engData.facilitatorName || 'Tamica Sears',
              facilitatorTitle: engData.facilitatorTitle || 'Founder, The Corporate Fixers',
              participantName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || user.email,
            });
          }

          setCertData({
            participantName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || user.email,
            programName: engData.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
            facilitatorName: engData.facilitatorName || 'Tamica Sears',
            facilitatorTitle: engData.facilitatorTitle || 'Founder, The Corporate Fixers',
            credentialId,
            completedAt: completedAt || new Date(),
          });
          setEarned(true);

          // Publish (or refresh) the public verification record for this
          // credential so the LinkedIn link has something to point at.
          try {
            await setDoc(doc(db, 'certVerifications', credentialId), {
              credentialId,
              credentialTitle: 'Certified AI Practitioner',
              participantName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || user.email,
              programName: engData.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
              completedAt: completedAt || new Date(),
            }, { merge: true });
          } catch { /* verification page is best-effort; certificate still works */ }
        }
      } catch (err) {
        console.error('Certificate check error:', err);
      }
      setLoading(false);
    };
    check();
  }, [user, engagementCode]);

  const handleDownload = async () => {
    if (!certData) return;
    setDownloading(true);
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'letter',
      });

      const W = pdf.internal.pageSize.getWidth();
      const H = pdf.internal.pageSize.getHeight();

      // Background
      pdf.setFillColor(242, 240, 236); // #F2F0EC
      pdf.rect(0, 0, W, H, 'F');

      // Outer border - navy
      pdf.setDrawColor(27, 42, 74);
      pdf.setLineWidth(3);
      pdf.rect(12, 12, W - 24, H - 24);

      // Inner border - gold
      pdf.setDrawColor(201, 168, 76);
      pdf.setLineWidth(1.5);
      pdf.rect(22, 22, W - 44, H - 44);

      // Gold corner accents
      const cornerSize = 18;
      pdf.setDrawColor(201, 168, 76);
      pdf.setLineWidth(2);
      // TL
      pdf.line(22, 22, 22 + cornerSize, 22);
      pdf.line(22, 22, 22, 22 + cornerSize);
      // TR
      pdf.line(W - 22 - cornerSize, 22, W - 22, 22);
      pdf.line(W - 22, 22, W - 22, 22 + cornerSize);
      // BL
      pdf.line(22, H - 22 - cornerSize, 22, H - 22);
      pdf.line(22, H - 22, 22 + cornerSize, H - 22);
      // BR
      pdf.line(W - 22 - cornerSize, H - 22, W - 22, H - 22);
      pdf.line(W - 22, H - 22 - cornerSize, W - 22, H - 22);

      // Navy header bar
      pdf.setFillColor(27, 42, 74);
      pdf.rect(12, 12, W - 24, 36, 'F');

      // Org name in header
      pdf.setTextColor(201, 168, 76);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('THE CORPORATE FIXERS', 36, 35);

      // Dot separator in header
      pdf.setFillColor(201, 168, 76);
      pdf.circle(W - 60, 30, 3, 'F');
      pdf.circle(W - 50, 30, 3, 'F');
      pdf.circle(W - 40, 30, 3, 'F');

      // "proudly presents this"
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text('PROUDLY PRESENTS THIS', W / 2, 80, { align: 'center' });

      // "CERTIFICATE OF COMPLETION"
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.text('CERTIFICATE OF COMPLETION', W / 2, 106, { align: 'center' });

      // Gold divider line
      pdf.setDrawColor(201, 168, 76);
      pdf.setLineWidth(1.5);
      pdf.line(W / 2 - 40, 116, W / 2 + 40, 116);

      // "AWARDED TO"
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text('AWARDED TO', W / 2, 140, { align: 'center' });

      // Participant name
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('times', 'bolditalic');
      pdf.setFontSize(36);
      pdf.text(certData.participantName, W / 2, 178, { align: 'center' });

      // Completion text
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('in recognition of the successful completion of all program requirements for', W / 2, 206, { align: 'center' });

      // Program name
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('times', 'italic');
      pdf.setFontSize(14);
      pdf.text(certData.programName, W / 2, 228, { align: 'center' });

      // Second line of completion text
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('demonstrating commitment to AI literacy and practical application in the workplace', W / 2, 248, { align: 'center' });

      // Divider line above signature row
      pdf.setDrawColor(27, 42, 74);
      pdf.setLineWidth(0.5);
      pdf.line(60, 288, W - 60, 288);

      // Signature block - left (script style signature above the line)
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('times', 'bolditalic');
      pdf.setFontSize(18);
      pdf.text(certData.facilitatorName, 150, 308, { align: 'center' });
      pdf.setDrawColor(27, 42, 74);
      pdf.setLineWidth(0.8);
      pdf.line(80, 316, 220, 316);
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      pdf.text(certData.facilitatorName, 150, 327, { align: 'center' });
      pdf.setTextColor(120, 120, 120);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text((certData.facilitatorTitle || 'Founder, The Corporate Fixers').toUpperCase(), 150, 338, { align: 'center' });

      // Seal - center
      pdf.setFillColor(27, 42, 74);
      pdf.circle(W / 2, 318, 30, 'F');
      pdf.setDrawColor(201, 168, 76);
      pdf.setLineWidth(1.5);
      pdf.circle(W / 2, 318, 30, 'S');
      pdf.setTextColor(201, 168, 76);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.text('TCF', W / 2, 315, { align: 'center' });
      pdf.setFontSize(6);
      pdf.text('CERTIFIED', W / 2, 325, { align: 'center' });

      // Date block - right
      pdf.setTextColor(120, 120, 120);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text('DATE OF COMPLETION', W - 150, 302, { align: 'center' });
      pdf.setDrawColor(27, 42, 74);
      pdf.setLineWidth(0.8);
      pdf.line(W - 220, 316, W - 80, 316);
      pdf.setTextColor(27, 42, 74);
      pdf.setFont('times', 'normal');
      pdf.setFontSize(11);
      pdf.text(formatDate(certData.completedAt), W - 150, 330, { align: 'center' });

      // Credential ID
      pdf.setTextColor(180, 180, 180);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.text(`CREDENTIAL ID: ${certData.credentialId}`, W / 2, H - 30, { align: 'center' });

      pdf.save(`TCF-Certificate-${certData.participantName.replace(/\s+/g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Could not generate PDF. Please try again.');
    }
    setDownloading(false);
  };

  const verifyUrl = certData ? `${window.location.origin}/#/verify/${encodeURIComponent(certData.credentialId)}` : '';

  const handleLinkedIn = () => {
    const d = certData.completedAt?.toDate ? certData.completedAt.toDate() : new Date(certData.completedAt);
    const url = 'https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME'
      + '&name=' + encodeURIComponent('Certified AI Practitioner')
      + '&organizationId=143077129'
      + '&issueYear=' + d.getFullYear()
      + '&issueMonth=' + (d.getMonth() + 1)
      + '&certUrl=' + encodeURIComponent(verifyUrl)
      + '&certId=' + encodeURIComponent(certData.credentialId);
    window.open(url, '_blank', 'noopener');
  };

  const handleAnnounce = () => {
    const text = "I just earned the Certified AI Practitioner credential from The Corporate Fixers! "
      + "Ten days of hands-on AI training through From Curious to Capable: real prompts built on my real work, "
      + "a redesigned workflow with measurable time savings, a working agent, and a custom tool I built myself. "
      + "Verify my credential: " + verifyUrl;
    window.open('https://www.linkedin.com/feed/?shareActive=true&text=' + encodeURIComponent(text), '_blank', 'noopener');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable */ }
  };

  const handleBadge = () => {
    const d = certData.completedAt?.toDate ? certData.completedAt.toDate() : new Date(certData.completedAt);
    const year = d.getFullYear();
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600">
      <circle cx="300" cy="300" r="290" fill="#1B2A4A" stroke="#C9A84C" stroke-width="10"/>
      <circle cx="300" cy="300" r="264" fill="none" stroke="#C9A84C" stroke-width="2"/>
      <text x="300" y="150" text-anchor="middle" fill="#C9A84C" font-family="Arial, sans-serif" font-size="17" font-weight="bold" letter-spacing="5">THE CORPORATE FIXERS</text>
      <text x="300" y="252" text-anchor="middle" fill="#C9A84C" font-family="Arial, sans-serif" font-size="30" font-weight="bold" letter-spacing="9">CERTIFIED</text>
      <text x="300" y="316" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="46" font-weight="bold" letter-spacing="2">AI PRACTITIONER</text>
      <rect x="252" y="348" width="96" height="3" fill="#C9A84C"/>
      <text x="300" y="398" text-anchor="middle" fill="#C9A84C" font-family="Georgia, serif" font-size="24" font-style="italic">From Curious to Capable</text>
      <text x="300" y="462" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="19" letter-spacing="3">${year}</text>
    </svg>`;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 1200;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, 1200, 1200);
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'Certified-AI-Practitioner-Badge.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  };

  if (loading) return null;
  if (!earned) return null;

  return (
    <div style={cs.wrapper}>
      <div style={cs.header}>
        <div style={cs.dayTag}>Achievement Unlocked</div>
        <div style={cs.title}>Your Certificate</div>
      </div>

      <div style={cs.body}>
        <div style={cs.certPreview}>
          {/* Certificate preview card */}
          <div style={cs.certCard}>
            <div style={cs.certTopBar}>
              <span style={cs.certOrg}>The Corporate Fixers</span>
              <div style={cs.dotRow}>
                <div style={cs.dot}></div>
                <div style={cs.dot}></div>
                <div style={cs.dot}></div>
              </div>
            </div>
            <div style={cs.certInner}>
              <p style={cs.presentsText}>proudly presents this</p>
              <p style={cs.certTitle}>Certificate of Completion</p>
              <div style={cs.divider}></div>
              <p style={cs.awardedTo}>awarded to</p>
              <p style={cs.nameText}>{certData.participantName}</p>
              <p style={cs.completionText}>in recognition of the successful completion of all program requirements for</p>
              <p style={cs.programText}>{certData.programName}</p>
              <div style={cs.certBottom}>
                <div style={cs.sigBlock}>
                  <p style={cs.sigScript}>{certData.facilitatorName}</p>
                  <div style={cs.sigLine}></div>
                  <p style={cs.sigName}>{certData.facilitatorName}</p>
                  <p style={cs.sigRole}>{certData.facilitatorTitle || 'Founder, The Corporate Fixers'}</p>
                </div>
                <div style={cs.seal}>
                  <div style={cs.sealCircle}>
                    <span style={cs.sealText}>TCF<br />Certified</span>
                  </div>
                </div>
                <div style={cs.dateBlock}>
                  <p style={cs.dateLabel}>date of completion</p>
                  <p style={cs.dateValue}>{formatDate(certData.completedAt)}</p>
                </div>
              </div>
              <p style={cs.credId}>credential id: {certData.credentialId}</p>
            </div>
          </div>
        </div>

        <div style={cs.actions}>
          <button style={cs.downloadBtn} onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Generating PDF...' : '⬇ Download Certificate'}
          </button>
          <p style={cs.note}>Your certificate is ready. Download and save it for your records.</p>

          <div style={cs.shareDivider}></div>
          <p style={cs.shareTitle}>Share your credential</p>
          <p style={cs.shareSub}>You earned the title Certified AI Practitioner. Put it where people can see it.</p>
          <div style={cs.shareRow}>
            <button style={cs.linkedInBtn} onClick={handleLinkedIn}>in&nbsp;&nbsp;Add to LinkedIn</button>
            <button style={cs.announceBtn} onClick={handleAnnounce}>📣 Announce It</button>
            <button style={cs.badgeBtn} onClick={handleBadge}>⬇ Download Badge</button>
            <button style={cs.copyBtn} onClick={handleCopyLink}>{copied ? '✓ Copied' : 'Copy verification link'}</button>
          </div>
          <p style={cs.shareNote}>
            Add to LinkedIn puts the credential on your profile under Licenses &amp; Certifications.
            Announce It opens a ready-to-post LinkedIn update, and attaching the badge image makes it pop.
            The verification link lets anyone confirm your credential is real.
          </p>
        </div>
      </div>
    </div>
  );
}

const cs = {
  wrapper: { fontFamily: 'DM Sans, sans-serif', marginBottom: '24px' },
  header: { background: '#1B2A4A', padding: '20px 28px', marginBottom: '0' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '4px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#fff' },
  body: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '28px' },

  certPreview: { marginBottom: '24px' },
  certCard: { background: '#F2F0EC', border: '3px solid #1B2A4A', maxWidth: '600px', margin: '0 auto' },
  certTopBar: { background: '#1B2A4A', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  certOrg: { fontFamily: 'Playfair Display, serif', fontSize: '11px', color: '#C9A84C', letterSpacing: '3px', textTransform: 'uppercase' },
  dotRow: { display: 'flex', gap: '4px', alignItems: 'center' },
  dot: { width: '5px', height: '5px', borderRadius: '50%', background: '#C9A84C' },
  certInner: { border: '1.5px solid #C9A84C', margin: '10px', padding: '20px 28px', position: 'relative' },

  presentsText: { fontFamily: 'DM Sans, sans-serif', fontSize: '9px', color: '#1B2A4A', letterSpacing: '3px', textTransform: 'uppercase', textAlign: 'center', margin: '0 0 4px' },
  certTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', letterSpacing: '3px', textTransform: 'uppercase', textAlign: 'center', margin: '0' },
  divider: { width: '48px', height: '2px', background: '#C9A84C', margin: '10px auto' },
  awardedTo: { fontFamily: 'DM Sans, sans-serif', fontSize: '9px', color: '#888', letterSpacing: '3px', textTransform: 'uppercase', textAlign: 'center', margin: '10px 0 4px' },
  nameText: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontStyle: 'italic', color: '#1B2A4A', textAlign: 'center', margin: '0 0 8px', lineHeight: '1.2' },
  completionText: { fontFamily: 'DM Sans, sans-serif', fontSize: '10px', color: '#555', textAlign: 'center', margin: '0 0 4px', lineHeight: '1.6' },
  programText: { fontFamily: 'Playfair Display, serif', fontSize: '12px', color: '#1B2A4A', fontStyle: 'italic', textAlign: 'center', margin: '0 0 16px' },

  certBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '12px', borderTop: '1px solid rgba(27,42,74,0.12)' },
  sigBlock: { textAlign: 'center', flex: 1 },
  sigScript: { fontFamily: 'Dancing Script, Brush Script MT, cursive', fontSize: '22px', color: '#1B2A4A', margin: '0 0 2px', lineHeight: '1.2' },
  sigLine: { width: '100px', height: '1px', background: '#1B2A4A', margin: '0 auto 4px' },
  sigName: { fontFamily: 'Playfair Display, serif', fontSize: '10px', color: '#1B2A4A', margin: '0' },
  sigRole: { fontFamily: 'DM Sans, sans-serif', fontSize: '8px', color: '#888', letterSpacing: '0.5px', textTransform: 'uppercase', margin: '2px 0 0' },
  seal: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  shareDivider: { height: '1px', background: '#E8E6E2', margin: '22px auto', maxWidth: '400px' },
  shareTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: '700', color: '#1B2A4A', textAlign: 'center', margin: '0 0 4px' },
  shareSub: { fontSize: '12.5px', color: '#555', textAlign: 'center', margin: '0 0 14px' },
  shareRow: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '12px' },
  linkedInBtn: { background: '#0A66C2', color: '#fff', border: 'none', padding: '11px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  announceBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '11px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  badgeBtn: { background: '#C9A84C', color: '#1B2A4A', border: 'none', padding: '11px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  copyBtn: { background: '#fff', color: '#1B2A4A', border: '1.5px solid #E8E6E2', padding: '11px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  shareNote: { fontSize: '11.5px', color: '#8A8A8A', textAlign: 'center', lineHeight: 1.6, maxWidth: '460px', margin: '0 auto' },
  sealCircle: { width: '46px', height: '46px', borderRadius: '50%', border: '2px solid #C9A84C', background: '#1B2A4A', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sealText: { fontFamily: 'Playfair Display, serif', fontSize: '7px', color: '#C9A84C', letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center', lineHeight: '1.4' },
  dateBlock: { textAlign: 'center', flex: 1 },
  dateLabel: { fontFamily: 'DM Sans, sans-serif', fontSize: '8px', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 3px' },
  dateValue: { fontFamily: 'Playfair Display, serif', fontSize: '10px', color: '#1B2A4A', margin: '0' },
  credId: { fontFamily: 'DM Sans, sans-serif', fontSize: '7px', color: '#bbb', textAlign: 'center', margin: '10px 0 0', letterSpacing: '0.5px' },

  actions: { textAlign: 'center' },
  downloadBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 32px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginBottom: '10px' },
  note: { fontSize: '11px', color: '#8A8A8A', fontStyle: 'italic', margin: '0' },
};
